import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

const siteOrigin = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://itaobuy.baby';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : undefined;

  const jsonLd = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: '2026-06-27',
    dateModified: '2026-06-27',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteOrigin}/article/${article.slug}`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Itaobuy',
      url: siteOrigin,
    },
    inLanguage: 'en',
  } : null;

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <div className="pt-24 md:pt-28 pb-20 px-6 md:px-10 bg-background-50 min-h-[60vh] flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-3xl font-heading font-bold text-foreground-950 mb-4">Article Not Found</h1>
              <p className="text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(slug);

  return (
    <>
      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-16 md:pb-20 px-6 md:px-10 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
            </div>

            <header className="mb-10 md:mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold uppercase tracking-wider mb-4">
                Article
              </span>
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span>{article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>{article.author}</span>
              </div>
            </header>

            <div className="mb-10 md:mb-12 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.imageAlt}
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="prose-custom">
              {article.sections.map((section, sIdx) => (
                <section key={sIdx} className="mb-10 md:mb-12">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-base text-foreground-700 leading-relaxed mb-4"
                      dangerouslySetInnerHTML={{ __html: para }}
                    ></p>
                  ))}
                </section>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-foreground-900">Ready to get started?</p>
                  <p className="text-sm text-foreground-600 mt-1">Access the full system on the main platform.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
                  >
                    Go to Itaobuy Sheet
                    <i className="ri-arrow-right-up-line ml-2"></i>
                  </a>
                  <Link
                    to="/"
                    className="px-6 py-2.5 rounded-full border-2 border-primary-500 text-primary-600 font-semibold text-sm hover:bg-primary-50 transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
                  >
                    <i className="ri-home-line mr-2"></i>
                    Back to Homepage
                  </Link>
                </div>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6">Related Articles</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group p-4 rounded-xl bg-background-100 border border-background-200 hover:border-primary-200 transition-all duration-300"
                    >
                      <div className="w-full h-32 rounded-lg overflow-hidden mb-3">
                        <img src={ra.image} alt={ra.imageAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <h4 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-700 transition-colors duration-200 line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-1">{ra.date}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10">
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-sm text-primary-600 hover:text-primary-700 transition-colors duration-200"
                  >
                    Home — Itaobuy Spreadsheet Overview
                  </Link>
                </li>
                <li>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-primary-600 hover:text-primary-700 transition-colors duration-200"
                  >
                    Main Platform — kaspreadsheet.com
                  </a>
                </li>
                <li>
                  <Link
                    to="/#shop-categories"
                    className="text-sm text-primary-600 hover:text-primary-700 transition-colors duration-200"
                  >
                    Shopping Guide — Browse Categories
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}